import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import "../../dp/pages/DpEco.css";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_BALANCO_LOJAS_PAGE, ESTOQUE_PAGE } from "../routes";
import { isAdminFromToken } from "../../../utils/authToken";
import { getStoreByIdApi } from "../../store/storeApi";
import {
  getCurrentBalanceApi,
  openBalanceApi,
  uploadFirstCountApi,
  uploadSaidasApi,
  uploadOcorrenciasApi,
  addManualOccurrenceApi,
  resolveDuplicatesApi,
  updateMassBalanceItemApi,
  setItemTrackedApi,
  teachPackApi,
  teachSaidaPackApi,
  teachOcorrenciaApi,
  unteachPackApi,
  dismissXmlUnmatchedApi,
  dismissSaidaUnmatchedApi,
  dismissSaidaErrosApi,
  dismissOcorrenciaUnmatchedApi,
  closeMassBalanceApi,
  MassBalance,
  MassBalanceItem,
  DuplicateDecision,
  AjusteEstoqueTipo,
  AJUSTE_TIPO_LABELS,
  AdjustmentType
} from "../api/massBalanceApi";
import { getProductReferencesApi, ProductReference } from "../api/productReferenceApi";

import { useGoBack } from "../../../hooks/useGoBack";

const OCORRENCIA_TIPO_LABELS: Record<AdjustmentType, string> = {
  AVARIA: "Avaria",
  VENCIMENTO: "Vencimento",
  CONSUMO: "Consumo",
  DEVOLUCAO: "Devolução",
};

export default function MassBalanceDetailsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(ESTOQUE_BALANCO_LOJAS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = isAdminFromToken(token);

  const [storeName, setStoreName] = useState("Loja");
  const [balance, setBalance] = useState<MassBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveAllProgress, setSaveAllProgress] = useState<{ done: number; total: number } | null>(null);

  // Draft inputs for items in the table
  const [drafts, setDrafts] = useState<Record<string, {
    contagem?: string;
    recontagem?: string;
    ajusteEstoque?: string;
    ajusteJustificativaTexto?: string;
    ajusteJustificativaTipo?: AjusteEstoqueTipo;
  }>>({});

  // Modals
  const [manualOccurOpen, setManualOccurOpen] = useState(false);
  const [teachPackOpen, setTeachPackOpen] = useState(false);
  const [teachSaidaOpen, setTeachSaidaOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  // Form states
  const [manualType, setManualType] = useState<AdjustmentType>("AVARIA");
  const [manualQty, setManualQty] = useState(1);
  const [manualEan, setManualEan] = useState("");
  const [manualObs, setManualObs] = useState("");

  const [packEan, setPackEan] = useState("");
  const [packSize, setPackSize] = useState(6);
  const [baseProductId, setBaseProductId] = useState("");

  const [saidaPackCod, setSaidaPackCod] = useState("");
  const [saidaPackSize, setSaidaPackSize] = useState(6);
  const [saidaBaseEan, setSaidaBaseEan] = useState("");

  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<string, "increment" | "replace" | "ignore"> >({});

  const [activeXmlUnmatched, setActiveXmlUnmatched] = useState<any | null>(null);
  const [activeSaidaUnmatched, setActiveSaidaUnmatched] = useState<any | null>(null);

  const [selectedAjusteItem, setSelectedAjusteItem] = useState<any | null>(null);
  const [ajusteModalOpen, setAjusteModalOpen] = useState(false);

  const [xmlIsPack, setXmlIsPack] = useState(true);
  const [xmlCodAdmin, setXmlCodAdmin] = useState("");
  const [xmlUnitDesc, setXmlUnitDesc] = useState("");
  const [xmlSearchQuery, setXmlSearchQuery] = useState("");
  const [xmlSearchResults, setXmlSearchResults] = useState<ProductReference[]>([]);
  const [xmlSearchLoading, setXmlSearchLoading] = useState(false);

  const [saidaIsPack, setSaidaIsPack] = useState(true);
  const [saidaEan, setSaidaEan] = useState("");
  const [saidaUnitDesc, setSaidaUnitDesc] = useState("");
  const [saidaSearchQuery, setSaidaSearchQuery] = useState("");
  const [saidaSearchResults, setSaidaSearchResults] = useState<ProductReference[]>([]);
  const [saidaSearchLoading, setSaidaSearchLoading] = useState(false);

  const [teachOcorrenciaOpen, setTeachOcorrenciaOpen] = useState(false);
  const [ocorrenciaCodAdmin, setOcorrenciaCodAdmin] = useState("");
  const [ocorrenciaMode, setOcorrenciaMode] = useState<"link" | "create">("link");
  const [ocorrenciaSelectedRef, setOcorrenciaSelectedRef] = useState<ProductReference | null>(null);
  const [ocorrenciaEan, setOcorrenciaEan] = useState("");
  const [ocorrenciaDesc, setOcorrenciaDesc] = useState("");
  const [ocorrenciaSearchQuery, setOcorrenciaSearchQuery] = useState("");
  const [ocorrenciaSearchResults, setOcorrenciaSearchResults] = useState<ProductReference[]>([]);
  const [ocorrenciaSearchLoading, setOcorrenciaSearchLoading] = useState(false);

  const draftFromItem = (it: MassBalanceItem) => ({
    contagem: it.contagem !== undefined && it.contagem !== null ? String(it.contagem) : "",
    recontagem: it.recontagem !== undefined && it.recontagem !== null ? String(it.recontagem) : "",
    ajusteEstoque: it.ajusteEstoque !== undefined && it.ajusteEstoque !== null ? String(it.ajusteEstoque) : "",
    ajusteJustificativaTexto: it.ajusteJustificativaTexto || "",
    ajusteJustificativaTipo: (it.ajusteJustificativaTipo || "EM_ANALISE") as AjusteEstoqueTipo,
  });

  // resetDraftIds: só esses itens têm o rascunho substituído pelos dados do servidor;
  // os demais preservam o que o usuário digitou. Sem o parâmetro, tudo é reiniciado.
  const loadData = async (opts?: { resetDraftIds?: string[] }) => {
    if (!token || !storeId) return;
    try {
      setLoading(true);
      const storeData = await getStoreByIdApi(token, storeId);
      setStoreName(storeData.nomeFantasia || "Loja");

      const activeBalance = await getCurrentBalanceApi(token, storeId);
      setBalance(activeBalance);

      // Prepopulate drafts with current item values
      if (activeBalance && activeBalance.items) {
        const resetIds = opts?.resetDraftIds;
        setDrafts((prev) => {
          const next: typeof drafts = {};
          activeBalance.items.forEach((it: MassBalanceItem) => {
            const keepUserDraft = resetIds && !resetIds.includes(it.id) && prev[it.id];
            next[it.id] = keepUserDraft ? prev[it.id] : draftFromItem(it);
          });
          return next;
        });
      }
    } catch (err: any) {
      toast.error(err.message || "Erro ao carregar dados do balanço.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }
    loadData();
  }, [token, storeId]);

  const handleOpenBalance = async () => {
    if (!token || !storeId) return;
    try {
      setLoading(true);
      await openBalanceApi(token, storeId);
      toast.success("Balanço aberto. Todos os produtos rastreados estão prontos para contagem.");
      await loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao abrir balanço.");
      setLoading(false);
    }
  };

  // Upload Actions
  const handleUploadXml = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !token || !storeId) return;
    try {
      toast.info("Importando XML...");
      const res = await uploadFirstCountApi(token, storeId, files[0]);
      toast.success(res.message || "XML importado com sucesso!");
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro na importação.");
    } finally {
      e.target.value = "";
    }
  };

  const handleUploadSaidas = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !token || !storeId) return;
    try {
      toast.info("Importando Saídas...");
      const res = await uploadSaidasApi(token, storeId, files[0]);
      toast.success(res.message || "Planilha de vendas importada!");
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro na importação.");
    } finally {
      e.target.value = "";
    }
  };

  const handleUploadOcorrencias = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !token || !storeId) return;
    try {
      toast.info("Importando Ocorrências...");
      const res = await uploadOcorrenciasApi(token, storeId, files[0]);
      toast.success(res.message || "Planilha de ocorrências importada!");
      if (res.unmatchedTypes && res.unmatchedTypes.length > 0) {
        toast.warn(`Tipos de movimentação não reconhecidos (linhas ignoradas): ${res.unmatchedTypes.join(", ")}`);
      }
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro na importação.");
    } finally {
      e.target.value = "";
    }
  };

  // Manual Occurrence Submit
  const handleAddManualOccurrence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !storeId) return;
    if (!manualEan.trim() || manualQty <= 0) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }
    try {
      await addManualOccurrenceApi(token, storeId, {
        codAdmin: manualEan.trim(),
        type: manualType,
        quantity: manualQty,
        observacao: manualObs,
      });
      toast.success("Ocorrência lançada com sucesso!");
      setManualOccurOpen(false);
      setManualEan("");
      setManualQty(1);
      setManualObs("");
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao registrar ocorrência.");
    }
  };

  // Match / Teach Pack Actions
  const handleSearchXmlProduct = async (queryStr: string) => {
    if (!token) return;
    if (!queryStr.trim()) {
      setXmlSearchResults([]);
      return;
    }
    try {
      setXmlSearchLoading(true);
      const res = await getProductReferencesApi(token, { query: queryStr, limit: 10 });
      setXmlSearchResults(res.data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setXmlSearchLoading(false);
    }
  };

  const handleSearchSaidaProduct = async (queryStr: string) => {
    if (!token) return;
    if (!queryStr.trim()) {
      setSaidaSearchResults([]);
      return;
    }
    try {
      setSaidaSearchLoading(true);
      const res = await getProductReferencesApi(token, { query: queryStr, limit: 10 });
      setSaidaSearchResults(res.data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setSaidaSearchLoading(false);
    }
  };

  const handleTeachPack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !balance || !activeXmlUnmatched) return;
    try {
      const payload = xmlIsPack
        ? {
            packEan: packEan.trim(),
            packSize: Number(packSize),
            baseProductId: baseProductId.trim(),
            deliveryQty: Number(activeXmlUnmatched.entrega),
            valorPorPack: Number(activeXmlUnmatched.valor),
            descricao: activeXmlUnmatched.descricao,
            numeroItem: `PACK-${packEan.trim()}`,
          }
        : {
            packEan: packEan.trim(),
            packSize: 1,
            baseProductId: packEan.trim(),
            deliveryQty: Number(activeXmlUnmatched.entrega),
            valorPorPack: Number(activeXmlUnmatched.valor),
            descricao: xmlUnitDesc.trim(),
            numeroItem: xmlCodAdmin.trim(),
          };

      await teachPackApi(token, balance.id, payload);
      toast.success(xmlIsPack ? "Pack ensinado e estoque atualizado!" : "Produto unitário cadastrado com sucesso!");
      setTeachPackOpen(false);
      setPackEan("");
      setBaseProductId("");
      setActiveXmlUnmatched(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao salvar.");
    }
  };

  const handleTeachSaidaPack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !balance || !activeSaidaUnmatched) return;
    try {
      const payload = saidaIsPack
        ? {
            packCodAdmin: saidaPackCod.trim(),
            packSize: Number(saidaPackSize),
            baseCodAdmin: saidaBaseEan.trim(),
            saidaQty: Number(activeSaidaUnmatched.qty),
            descricao: activeSaidaUnmatched.descricao,
          }
        : {
            packCodAdmin: saidaPackCod.trim(),
            packSize: 1,
            baseCodAdmin: saidaPackCod.trim(),
            saidaQty: Number(activeSaidaUnmatched.qty),
            descricao: saidaUnitDesc.trim(),
          };

      await teachSaidaPackApi(token, balance.id, payload);
      toast.success(saidaIsPack ? "Pack de saída ensinado com sucesso!" : "Produto de saída unitário cadastrado com sucesso!");
      setTeachSaidaOpen(false);
      setSaidaPackCod("");
      setSaidaBaseEan("");
      setActiveSaidaUnmatched(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao ensinar pack.");
    }
  };

  const handleSearchOcorrenciaProduct = async (queryStr: string) => {
    if (!token) return;
    if (!queryStr.trim()) {
      setOcorrenciaSearchResults([]);
      return;
    }
    try {
      setOcorrenciaSearchLoading(true);
      const res = await getProductReferencesApi(token, { query: queryStr, limit: 10 });
      setOcorrenciaSearchResults(res.data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setOcorrenciaSearchLoading(false);
    }
  };

  const handleTeachOcorrencia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !balance) return;
    try {
      if (ocorrenciaMode === "link") {
        if (!ocorrenciaSelectedRef) {
          toast.error("Busque e selecione o produto para vincular.");
          return;
        }
        await teachOcorrenciaApi(token, balance.id, {
          codAdmin: ocorrenciaCodAdmin.trim(),
          mode: "link",
          productReferenceId: ocorrenciaSelectedRef.id,
        });
      } else {
        await teachOcorrenciaApi(token, balance.id, {
          codAdmin: ocorrenciaCodAdmin.trim(),
          mode: "create",
          ean: ocorrenciaEan.trim(),
          descricao: ocorrenciaDesc.trim(),
        });
      }
      toast.success("De/Para vinculado e ocorrências aplicadas ao balanço!");
      setTeachOcorrenciaOpen(false);
      setOcorrenciaCodAdmin("");
      setOcorrenciaSelectedRef(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao vincular De/Para da ocorrência.");
    }
  };

  const handleUnteachPack = async (ean: string) => {
    if (!token || !balance) return;
    if (!window.confirm(`Deseja desfazer a vinculação do pack ${ean}?`)) return;
    try {
      await unteachPackApi(token, balance.id, ean);
      toast.success("Vinculação de pack removida.");
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao reverter pack.");
    }
  };

  const handleResolveDuplicates = async () => {
    if (!token || !balance) return;
    const decisions: DuplicateDecision[] = Object.entries(duplicateDecisions).map(([codAdmin, decision]) => ({
      codAdmin,
      decision,
    }));

    if (decisions.length === 0) {
      toast.error("Nenhuma decisão selecionada.");
      return;
    }

    try {
      await resolveDuplicatesApi(token, balance.id, decisions);
      toast.success("Duplicidades de saídas resolvidas com sucesso!");
      setDuplicateOpen(false);
      setDuplicateDecisions({});
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao resolver duplicidades.");
    }
  };

  // Dismiss Notifications/Warnings
  const handleDismissXml = async () => {
    if (!token || !balance) return;
    try {
      await dismissXmlUnmatchedApi(token, balance.id);
      toast.success("Avisos de XML dispensados.");
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDismissSaidas = async () => {
    if (!token || !balance) return;
    try {
      await dismissSaidaUnmatchedApi(token, balance.id);
      toast.success("Avisos de multipack de saídas dispensados.");
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDismissSaidaErros = async () => {
    if (!token || !balance) return;
    try {
      await dismissSaidaErrosApi(token, balance.id);
      toast.success("Erros de De/Para de saídas dispensados.");
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDismissOcorrencias = async () => {
    if (!token || !balance) return;
    try {
      await dismissOcorrenciaUnmatchedApi(token, balance.id);
      toast.success("Avisos de ocorrências dispensados.");
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Toggle item tracking tag
  const handleToggleTracking = async (itemId: string, isTracked: boolean) => {
    if (!token || !balance) return;
    try {
      await setItemTrackedApi(token, balance.id, itemId, isTracked);
      toast.success("Rastreamento atualizado para este item.");
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao alterar rastreamento.");
    }
  };

  // Row update handlers
  const renderStatusBadges = (item: any) => {
    const badges: React.ReactNode[] = [];

    // First count status
    if (item.contagemSetAt !== null) {
      if (Number(item.variacaoContagem) === 0) {
        badges.push(
          <span key="c1" className="estoqueBadge estoqueBadge--green">
            Conferido
          </span>
        );
      } else {
        badges.push(
          <span key="c1" className="estoqueBadge estoqueBadge--red">
            Divergência
          </span>
        );
      }
    }

    // Second count status
    if (item.contagemSetAt !== null && Number(item.variacaoContagem) !== 0) {
      if (item.recontagemSetAt !== null) {
        if (Number(item.variacaoRecontagem) === 0) {
          badges.push(
            <span key="c2" className="estoqueBadge estoqueBadge--green" style={{ background: "var(--eco-status-good-soft)", color: "var(--eco-status-good)" }}>
              Reconferido
            </span>
          );
        } else {
          badges.push(
            <span key="c2" className="estoqueBadge estoqueBadge--red">
              Divergência
            </span>
          );
        }
      }
    }

    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {badges.length === 0 ? (
          <span className="estoqueBadge" style={{ background: "var(--eco-ground)", color: "var(--eco-muted)" }}>
            Pendente
          </span>
        ) : (
          badges
        )}
      </div>
    );
  };
  const handleDraftChange = (itemId: string, field: string, value: any) => {
    setDrafts((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const buildItemDto = (item: MassBalanceItem, draft?: (typeof drafts)[string]) => {
    if (!draft) return null;

    const dto: any = {};
    const originalContagem = item.contagem !== null && item.contagem !== undefined ? Number(item.contagem) : null;
    const originalRecontagem = item.recontagem !== null && item.recontagem !== undefined ? Number(item.recontagem) : null;
    const originalAjuste = item.ajusteEstoque !== null && item.ajusteEstoque !== undefined ? Number(item.ajusteEstoque) : null;
    const originalJustificativa = item.ajusteJustificativaTexto || "";
    const originalTipo = item.ajusteJustificativaTipo || "EM_ANALISE";

    if (draft.contagem !== "" && Number(draft.contagem) !== originalContagem && originalContagem === null) {
      dto.contagem = Number(draft.contagem);
    }
    if (draft.recontagem !== "" && Number(draft.recontagem) !== originalRecontagem && originalRecontagem === null) {
      dto.recontagem = Number(draft.recontagem);
    }
    if (
      originalAjuste === null && (
        (draft.ajusteEstoque !== "" && Number(draft.ajusteEstoque) !== originalAjuste) ||
        (draft.ajusteJustificativaTexto !== originalJustificativa) ||
        (draft.ajusteJustificativaTipo !== originalTipo)
      )
    ) {
      dto.ajusteEstoque = Number(draft.ajusteEstoque);
      dto.ajusteJustificativaTexto = draft.ajusteJustificativaTexto;
      dto.ajusteJustificativaTipo = draft.ajusteJustificativaTipo;
    }

    return dto;
  };

  const handleSaveItem = async (item: MassBalanceItem) => {
    if (!token || !balance) return;
    const dto = buildItemDto(item, drafts[item.id]);
    if (!dto) return;

    try {
      await updateMassBalanceItemApi(token, balance.id, item.id, dto);
      toast.success("Dados do item salvos com sucesso!");
      loadData({ resetDraftIds: [item.id] });
    } catch (err: any) {
      toast.error(err.message || "Erro ao salvar contagem.");
    }
  };

  const handleSaveAll = async () => {
    if (!token || !balance) return;

    const pending = (balance.items || [])
      .filter((item) => item.isTracked)
      .map((item) => ({ item, dto: buildItemDto(item, drafts[item.id]) }))
      .filter((p): p is { item: MassBalanceItem; dto: any } => !!p.dto && Object.keys(p.dto).length > 0);

    if (pending.length === 0) {
      toast.info("Nenhuma alteração pendente para salvar.");
      return;
    }

    setSaveAllProgress({ done: 0, total: pending.length });
    const savedIds: string[] = [];
    const errors: string[] = [];

    for (let i = 0; i < pending.length; i++) {
      const { item, dto } = pending[i];
      try {
        await updateMassBalanceItemApi(token, balance.id, item.id, dto);
        savedIds.push(item.id);
      } catch (err: any) {
        errors.push(`${item.descricao}: ${err.message || "erro ao salvar"}`);
      }
      setSaveAllProgress({ done: i + 1, total: pending.length });
    }

    setSaveAllProgress(null);
    if (errors.length === 0) {
      toast.success(`${savedIds.length === 1 ? "1 item salvo" : `${savedIds.length} itens salvos`} com sucesso!`);
    } else {
      toast.warn(`${savedIds.length} de ${pending.length} itens salvos. Falhas: ${errors.length}`);
      errors.forEach((msg) => toast.error(msg));
    }
    // itens com falha mantêm o rascunho para correção e nova tentativa
    loadData({ resetDraftIds: savedIds });
  };

  // Close Cycle
  // Confirmação no diálogo do sistema (era `window.confirm`, 15/10/2026).
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleCloseBalance = async () => {
    if (!token || !balance) return;
    try {
      setClosing(true);
      await closeMassBalanceApi(token, balance.id);
      toast.success("Ciclo de Balanço Encerrado!");
      setConfirmClose(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Erro ao encerrar balanço.");
    } finally {
      setClosing(false);
    }
  };

  const allItems = balance?.items || [];
  const trackedItems = allItems.filter((item) => item.isTracked);
  const nonTracked = allItems.filter((item) => !item.isTracked);
  // Packs com unitário vinculado: são apenas referência (a contagem vai no unitário).
  const isPackItem = (item: any) => Number(item.packSize) > 1 && !!item.baseProductId;
  const packItems = nonTracked.filter((item) => isPackItem(item));
  const nonTrackedItems = nonTracked.filter((item) => !isPackItem(item));
  const baseDescricaoOf = (item: any) =>
    (allItems as any[]).find((x) => x.sku === item.baseProductId)?.descricao || `EAN ${item.baseProductId}`;

  // ocorrências sem De/Para agrupadas por Cod Admin; a dispensa zera o contador e esconde o aviso
  const unmatchedOcorrencias = (() => {
    if (!balance || !balance.ocorrenciaUnmatchedCount) return [] as { codAdmin: string; tipos: string }[];
    const byCod: Record<string, Partial<Record<AdjustmentType, number>>> = {};
    for (const occ of balance.occurrences || []) {
      if (occ.productRefId || !occ.codAdmin) continue;
      const qtyByTipo = byCod[occ.codAdmin] ?? (byCod[occ.codAdmin] = {});
      qtyByTipo[occ.type] = (qtyByTipo[occ.type] ?? 0) + Number(occ.quantity);
    }
    return Object.entries(byCod).map(([codAdmin, qtyByTipo]) => ({
      codAdmin,
      tipos: Object.entries(qtyByTipo)
        .map(([t, q]) => `${OCORRENCIA_TIPO_LABELS[t as AdjustmentType]}: ${q}`)
        .join(" · "),
    }));
  })();

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[
              ...(isAdmin ? [{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }] : [{ label: "Estoque" }]),
              { label: "Balanço de massa", onClick: () => navigate(ESTOQUE_BALANCO_LOJAS_PAGE()) },
              { label: storeName || "Loja" },
            ]}
            title={`Balanço diário: ${storeName}`}
            subtitle={
              balance ? (
                <>
                  Responsável: <strong>{balance.createdBy?.nomeCompleto || "Desconhecido"}</strong> · Referência:{" "}
                  {new Date(balance.referenceDate).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
                </>
              ) : undefined
            }
            onBack={goBack}
            backLabel="Voltar para as lojas"
            actions={
              balance ? (
                <>
                  <span
                    className={`estoqueBadge ${balance.status === "CLOSED" ? "estoqueBadge--green" : "estoqueBadge--blue"} dpeHeaderBadge`}
                  >
                    {balance.status === "CLOSED" ? "Encerrado" : balance.status === "RECOUNTED" ? "Recontado" : "Em andamento"}
                  </span>
                  {balance.status !== "CLOSED" ? (
                    <>
                      <button type="button" className="ecoBtn" onClick={() => setManualOccurOpen(true)}>
                        Ocorrência manual
                      </button>
                      <button type="button" className="ecoBtn ecoBtn--dangerSolid" onClick={() => setConfirmClose(true)}>
                        Encerrar balanço
                      </button>
                    </>
                  ) : null}
                </>
              ) : undefined
            }
          />

          {loading && !balance && (
            <div style={{ textAlign: "center", padding: 40 }}>Carregando dados...</div>
          )}

          {/* Initial State dashboard - Choose file to start */}
          {!balance && !loading && (
            <div style={{ padding: 40, textAlign: "center", background: "var(--eco-ground)", borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 16px 0", color: "var(--eco-ink)" }}>Iniciar Balanço Diário</h3>

              <div style={{ marginBottom: 28 }}>
                <p style={{ color: "var(--eco-muted)", marginBottom: 12 }}>
                  Abra o balanço para contar <b>todos os produtos rastreados</b> da loja (mesmo sem movimentação):
                </p>
                <button
                  className="estoqueBtn estoqueBtn--primary"
                  style={{ padding: "12px 24px", height: "auto" }}
                  onClick={handleOpenBalance}
                >
                  Abrir Balanço
                </button>
              </div>

              <p style={{ color: "var(--eco-muted)", marginBottom: 24 }}>Ou importe um dos arquivos abaixo para iniciar o balanço da loja de hoje:</p>

              <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
                <label className="estoqueBtn estoqueBtn--primary" style={{ padding: "12px 24px", height: "auto" }}>
                  NF-e de Entradas (XML)
                  <input
                    type="file"
                    accept=".xml"
                    onChange={handleUploadXml}
                    style={{ display: "none" }}
                  />
                </label>

                <label className="estoqueBtn estoqueBtn--secondary" style={{ padding: "12px 24px", height: "auto" }}>
                  Saídas (Excel)
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleUploadSaidas}
                    style={{ display: "none" }}
                  />
                </label>

                <label className="estoqueBtn estoqueBtn--secondary" style={{ padding: "12px 24px", height: "auto" }}>
                  Ocorrências (Excel)
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleUploadOcorrencias}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>
          )}

          {balance && balance.status === "CLOSED" && !loading && (
            <div style={{ padding: 24, textAlign: "center", background: "var(--eco-ground)", borderRadius: 12, marginBottom: 24, border: "1px dashed #cbd5e1" }}>
              <h3 style={{ margin: "0 0 8px 0", color: "var(--eco-ink)" }}>Iniciar novo balanço</h3>
              <p style={{ color: "var(--eco-muted)", marginBottom: 20 }}>
                Este balanço está encerrado. Abra um novo (referência: dia seguinte) ou importe um dos arquivos abaixo. É permitido apenas um balanço por loja por dia.
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
                <button
                  className="estoqueBtn estoqueBtn--primary"
                  style={{ padding: "12px 24px", height: "auto" }}
                  onClick={handleOpenBalance}
                >
                  Abrir Balanço
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
                <label className="estoqueBtn estoqueBtn--secondary" style={{ padding: "12px 24px", height: "auto" }}>
                  NF-e de Entradas (XML)
                  <input type="file" accept=".xml" onChange={handleUploadXml} style={{ display: "none" }} />
                </label>
                <label className="estoqueBtn estoqueBtn--secondary" style={{ padding: "12px 24px", height: "auto" }}>
                  Saídas (Excel)
                  <input type="file" accept=".xlsx, .xls" onChange={handleUploadSaidas} style={{ display: "none" }} />
                </label>
                <label className="estoqueBtn estoqueBtn--secondary" style={{ padding: "12px 24px", height: "auto" }}>
                  Ocorrências (Excel)
                  <input type="file" accept=".xlsx, .xls" onChange={handleUploadOcorrencias} style={{ display: "none" }} />
                </label>
              </div>
            </div>
          )}

          {balance && (
            <>
              {/* Uploads and merge section */}
              {balance.status !== "CLOSED" && (
                <div style={{ background: "var(--eco-ground)", padding: 16, borderRadius: 8, marginBottom: 24, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--eco-ink-2)" }}>Mesclar mais arquivos:</span>
                  <label className="estoqueBtn estoqueBtn--secondary" style={{ height: 32, fontSize: 13 }}>
                    Importar Entradas (XML)
                    <input type="file" accept=".xml" onChange={handleUploadXml} style={{ display: "none" }} />
                  </label>
                  <label className="estoqueBtn estoqueBtn--secondary" style={{ height: 32, fontSize: 13 }}>
                    Importar Saídas (Excel)
                    <input type="file" accept=".xlsx, .xls" onChange={handleUploadSaidas} style={{ display: "none" }} />
                  </label>
                  <label className="estoqueBtn estoqueBtn--secondary" style={{ height: 32, fontSize: 13 }}>
                    Importar Ocorrências (Excel)
                    <input type="file" accept=".xlsx, .xls" onChange={handleUploadOcorrencias} style={{ display: "none" }} />
                  </label>
                </div>
              )}

              {/* Match Resolution Alerts */}
              {balance.status !== "CLOSED" && (
                <>
                  {/* XML Unmatched entries */}
                  {balance.xmlUnmatchedEntries && balance.xmlUnmatchedEntries.length > 0 && (
                    <div className="unmatchedSection">
                      <h4 className="unmatchedSection__title">Itens do XML sem De/Para ({balance.xmlUnmatchedEntries.length})</h4>
                      <div className="unmatchedList">
                        {balance.xmlUnmatchedEntries.map((xml) => (
                          <div key={xml.id} className="unmatchedItem">
                            <span className="unmatchedItem__info">
                              SKU/EAN: <strong>{xml.sku}</strong> | {xml.descricao} | Entrega: {xml.entrega} un
                              {xml.isLikelyPack && (
                                <span className="estoqueBadge estoqueBadge--orange" style={{ marginLeft: 8 }}>
                                  Possível Pack c/{xml.detectedPackSize || "?"}
                                </span>
                              )}
                            </span>
                            <div className="unmatchedItem__actions">
                              <button
                                className="unmatchedItem__btn unmatchedItem__btn--primary"
                                onClick={() => {
                                  setPackEan(xml.sku);
                                  setPackSize(xml.detectedPackSize || 6);
                                  setBaseProductId("");
                                  setActiveXmlUnmatched(xml);
                                  setXmlIsPack(true);
                                  setXmlCodAdmin(xml.sku);
                                  setXmlUnitDesc(xml.descricao);
                                  setXmlSearchQuery("");
                                  setXmlSearchResults([]);
                                  setTeachPackOpen(true);
                                }}
                              >
                                Ensinar Pack
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        className="estoqueBtn estoqueBtn--secondary"
                        style={{ height: 30, marginTop: 8, alignSelf: "flex-start" }}
                        onClick={handleDismissXml}
                      >
                        Dispensar Avisos de XML
                      </button>
                    </div>
                  )}

                  {/* Saidas Unmatched entries */}
                  {balance.saidaUnmatchedEntries && balance.saidaUnmatchedEntries.length > 0 && (
                    <div className="unmatchedSection">
                      <h4 className="unmatchedSection__title">Multipacks não identificados nas saídas ({balance.saidaUnmatchedEntries.length})</h4>
                      <div className="unmatchedList">
                        {balance.saidaUnmatchedEntries.map((s) => (
                          <div key={s.id} className="unmatchedItem">
                            <span className="unmatchedItem__info">
                              Cod Admin: <strong>{s.codAdmin}</strong> | {s.descricao} | Vendas: {s.qty} un
                              {s.isLikelyPack && (
                                <span className="estoqueBadge estoqueBadge--orange" style={{ marginLeft: 8 }}>
                                  Possível Pack c/{s.detectedPackSize || "?"}
                                </span>
                              )}
                            </span>
                            <div className="unmatchedItem__actions">
                              <button
                                className="unmatchedItem__btn unmatchedItem__btn--primary"
                                onClick={() => {
                                  setSaidaPackCod(s.codAdmin || "");
                                  setSaidaPackSize(s.detectedPackSize || 6);
                                  setSaidaBaseEan("");
                                  setActiveSaidaUnmatched(s);
                                  setSaidaIsPack(true);
                                  setSaidaEan("");
                                  setSaidaUnitDesc(s.descricao);
                                  setSaidaSearchQuery("");
                                  setSaidaSearchResults([]);
                                  setTeachSaidaOpen(true);
                                }}
                              >
                                Ensinar Pack
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        className="estoqueBtn estoqueBtn--secondary"
                        style={{ height: 30, marginTop: 8, alignSelf: "flex-start" }}
                        onClick={handleDismissSaidas}
                      >
                        Dispensar Avisos de Saídas Multipack
                      </button>
                    </div>
                  )}

                  {/* Ocorrências Unmatched entries */}
                  {unmatchedOcorrencias.length > 0 && (
                    <div className="unmatchedSection">
                      <h4 className="unmatchedSection__title">Ocorrências sem De/Para ({unmatchedOcorrencias.length})</h4>
                      <div className="unmatchedList">
                        {unmatchedOcorrencias.map((o) => (
                          <div key={o.codAdmin} className="unmatchedItem">
                            <span className="unmatchedItem__info">
                              Cod Admin: <strong>{o.codAdmin}</strong> | {o.tipos}
                            </span>
                            <div className="unmatchedItem__actions">
                              <button
                                className="unmatchedItem__btn unmatchedItem__btn--primary"
                                onClick={() => {
                                  setOcorrenciaCodAdmin(o.codAdmin);
                                  setOcorrenciaMode("link");
                                  setOcorrenciaSelectedRef(null);
                                  setOcorrenciaEan("");
                                  setOcorrenciaDesc("");
                                  setOcorrenciaSearchQuery("");
                                  setOcorrenciaSearchResults([]);
                                  setTeachOcorrenciaOpen(true);
                                }}
                              >
                                Vincular De/Para
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        className="estoqueBtn estoqueBtn--secondary"
                        style={{ height: 30, marginTop: 8, alignSelf: "flex-start" }}
                        onClick={handleDismissOcorrencias}
                      >
                        Dispensar Avisos de Ocorrências
                      </button>
                    </div>
                  )}

                  {/* Vendas sem produto cadastrado (não casadas) */}
                  {Array.isArray(balance.saidaErrosDePara) && balance.saidaErrosDePara.length > 0 && (
                    <div className="unmatchedSection" style={{ background: "var(--eco-status-warn-soft)", borderColor: "#fcd34d" }}>
                      <h4 className="unmatchedSection__title" style={{ color: "#6e3b05" }}>
                        Vendas sem produto cadastrado ({balance.saidaErrosDePara.length})
                      </h4>
                      <p style={{ fontSize: 13, color: "#6e3b05", margin: "4px 0 12px 0" }}>
                        Estas linhas de venda não têm um produto correspondente no cadastro e não entraram no balanço. Revise o cadastro de produtos ou dispense o aviso.
                      </p>
                      <button
                        className="estoqueBtn estoqueBtn--secondary"
                        style={{ height: 32, alignSelf: "flex-start" }}
                        onClick={() => setDuplicateOpen(true)}
                      >
                        Ver detalhes
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Tracked Items Table */}
              <div className="balTrack__head">
                <h3 className="balTrack__title">Produtos Rastreados no Balanço</h3>
                {trackedItems.length > 0 ? (
                  <span className="balTrack__progress">
                    <span className="balTrack__progressBar" aria-hidden="true">
                      <span
                        className="balTrack__progressFill"
                        style={{
                          width: `${(trackedItems.filter((i) => i.contagemSetAt !== null && i.contagemSetAt !== undefined).length / trackedItems.length) * 100}%`,
                        }}
                      />
                    </span>
                    {trackedItems.filter((i) => i.contagemSetAt !== null && i.contagemSetAt !== undefined).length} de{" "}
                    {trackedItems.length} contados
                  </span>
                ) : null}
              </div>
              <div className="estoqueTableWrapper balTrack__wrap">
                <table className="estoqueTable balTrack">
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th>Status</th>
                      <th>Contagem</th>
                      <th>Segunda Contagem</th>
                      <th>Ajuste de Estoque</th>
                      <th style={{ width: 100, textAlign: "center" }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackedItems.length === 0 ? (
                      <tr className="balTrack__emptyRow">
                        <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--eco-muted)" }}>
                          Nenhum produto sendo rastreado neste balanço. Ative o rastreamento na lista de não rastreados abaixo.
                        </td>
                      </tr>
                    ) : (
                      trackedItems.map((item) => {
                        const draft = drafts[item.id] || {};
                        const isClosed = balance.status === "CLOSED";

                        const contagemEditable =
                          !isClosed &&
                          (item.contagemSetAt === null || item.contagemSetAt === undefined);

                        const recontagemEditable =
                          !isClosed &&
                          item.contagemSetAt !== null &&
                          Number(item.variacaoContagem) !== 0 &&
                          (item.recontagemSetAt === null || item.recontagemSetAt === undefined);

                        const ajusteEditable =
                          !isClosed &&
                          item.recontagemSetAt !== null &&
                          Number(item.variacaoRecontagem) !== 0 &&
                          (item.ajusteEstoqueSetAt === null || item.ajusteEstoqueSetAt === undefined);

                        const hasChanged =
                          (draft.contagem !== (item.contagem !== null && item.contagem !== undefined ? String(item.contagem) : "")) ||
                          (draft.recontagem !== (item.recontagem !== null && item.recontagem !== undefined ? String(item.recontagem) : "")) ||
                          (draft.ajusteEstoque !== (item.ajusteEstoque !== null && item.ajusteEstoque !== undefined ? String(item.ajusteEstoque) : "")) ||
                          (draft.ajusteJustificativaTexto !== (item.ajusteJustificativaTexto || "")) ||
                          (draft.ajusteJustificativaTipo !== (item.ajusteJustificativaTipo || "EM_ANALISE"));

                        // No celular, só aparecem as etapas que já existem para o produto.
                        const hasRecount = item.contagemSetAt !== null && Number(item.variacaoContagem) !== 0;
                        const hasAdjust = item.recontagemSetAt !== null && Number(item.variacaoRecontagem) !== 0;

                        return (
                          <tr
                            key={item.id}
                            className={`balTrack__row${hasAdjust && item.ajusteEstoqueSetAt === null ? " balTrack__row--adjust" : hasRecount && item.recontagemSetAt === null ? " balTrack__row--recount" : ""}`}
                          >
                            <td className="balTrack__product">
                              <div style={{ fontWeight: 600, color: "var(--eco-ink)" }}>{item.descricao}</div>
                              <div style={{ fontSize: 11, color: "var(--eco-muted)", marginTop: 2 }}>EAN: {item.sku}</div>
                            </td>
                            <td className="balTrack__status">{renderStatusBadges(item)}</td>
                            <td data-label="Contagem">
                              {item.contagemSetAt === null ? (
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  className="estoqueInput balTrack__num"
                                  aria-label={`Contagem de ${item.descricao}`}
                                  placeholder="Qtd"
                                  disabled={!contagemEditable}
                                  value={draft.contagem || ""}
                                  onChange={(e) => handleDraftChange(item.id, "contagem", e.target.value)}
                                />
                              ) : (
                                <span style={{ fontWeight: 500 }}>{item.contagem}</span>
                              )}
                            </td>
                            <td data-label="2ª contagem" className={hasRecount ? undefined : "balTrack__optional"}>
                              {item.contagemSetAt === null ? (
                                <span style={{ color: "var(--eco-border)" }}>—</span>
                              ) : Number(item.variacaoContagem) === 0 ? (
                                <span
                                  className="estoqueBadge"
                                  style={{ background: "var(--eco-status-good-soft)", color: "var(--eco-status-good)", display: "inline-block", fontWeight: 600 }}
                                >
                                  Sem divergência
                                </span>
                              ) : item.recontagemSetAt === null ? (
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  className="estoqueInput balTrack__num"
                                  aria-label={`Segunda contagem de ${item.descricao}`}
                                  placeholder="Qtd"
                                  disabled={!recontagemEditable}
                                  value={draft.recontagem || ""}
                                  onChange={(e) => handleDraftChange(item.id, "recontagem", e.target.value)}
                                />
                              ) : (
                                <span style={{ fontWeight: 500 }}>{item.recontagem}</span>
                              )}
                            </td>
                            <td data-label="Ajuste de estoque" className={hasAdjust ? "balTrack__adjustCell" : "balTrack__optional"}>
                              {item.recontagemSetAt !== null && Number(item.variacaoRecontagem) !== 0 ? (
                                item.ajusteEstoqueSetAt === null ? (
                                  <div className="balTrack__adjust">
                                    <input
                                      type="number"
                                      inputMode="numeric"
                                      placeholder="Qtd"
                                      aria-label={`Quantidade do ajuste de ${item.descricao}`}
                                      className="estoqueInput balTrack__num"
                                      disabled={!ajusteEditable}
                                      value={draft.ajusteEstoque || ""}
                                      onChange={(e) => handleDraftChange(item.id, "ajusteEstoque", e.target.value)}
                                    />
                                    <select
                                      className="estoqueSelect balTrack__select"
                                      aria-label={`Tipo do ajuste de ${item.descricao}`}
                                      disabled={!ajusteEditable}
                                      value={draft.ajusteJustificativaTipo || "EM_ANALISE"}
                                      onChange={(e) => handleDraftChange(item.id, "ajusteJustificativaTipo", e.target.value)}
                                    >
                                      {Object.entries(AJUSTE_TIPO_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                      ))}
                                    </select>
                                    <input
                                      type="text"
                                      placeholder="Justificativa"
                                      aria-label={`Justificativa do ajuste de ${item.descricao}`}
                                      className="estoqueInput balTrack__text"
                                      disabled={!ajusteEditable}
                                      value={draft.ajusteJustificativaTexto || ""}
                                      onChange={(e) => handleDraftChange(item.id, "ajusteJustificativaTexto", e.target.value)}
                                    />
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="balTrack__adjustLink"
                                    onClick={() => {
                                      setSelectedAjusteItem(item);
                                      setAjusteModalOpen(true);
                                    }}
                                  >
                                    {item.ajusteEstoque} {item.ajusteJustificativaTexto ? ` - ${item.ajusteJustificativaTexto}` : ""}
                                  </button>
                                )
                              ) : (
                                <span style={{ color: "var(--eco-border)" }}>—</span>
                              )}
                            </td>
                            <td className={`balTrack__action${hasChanged ? "" : " balTrack__optional"}`}>
                              {hasChanged ? (
                                <button
                                  className="estoqueBtn estoqueBtn--primary balTrack__save"
                                  disabled={!!saveAllProgress}
                                  onClick={() => handleSaveItem(item)}
                                >
                                  Salvar
                                </button>
                              ) : (
                                <span style={{ color: "var(--eco-border)" }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {balance.status !== "CLOSED" && trackedItems.length > 0 && (
                <div className="balTrack__saveAll">
                  {saveAllProgress && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 180, height: 8, background: "var(--eco-border-soft)", borderRadius: 4, overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${Math.round((saveAllProgress.done / saveAllProgress.total) * 100)}%`,
                            height: "100%",
                            background: "var(--eco-primary)",
                            borderRadius: 4,
                            transition: "width .2s ease",
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--eco-ink-2)", minWidth: 40 }}>
                        {Math.round((saveAllProgress.done / saveAllProgress.total) * 100)}%
                      </span>
                    </div>
                  )}
                  <button
                    className="estoqueBtn estoqueBtn--primary"
                    style={{ height: 36, padding: "0 20px" }}
                    disabled={!!saveAllProgress}
                    onClick={handleSaveAll}
                  >
                    {saveAllProgress
                      ? `Salvando... (${saveAllProgress.done}/${saveAllProgress.total})`
                      : "Salvar tudo"}
                  </button>
                </div>
              )}

              {/* Packs (referência — contados no unitário) */}
              {packItems.length > 0 && (
                <>
                  <h3 style={{ margin: "32px 0 4px 0", fontSize: 18, color: "var(--eco-ink)" }}>Packs (referência)</h3>
                  <p style={{ margin: "0 0 12px 0", fontSize: 13, color: "var(--eco-muted)" }}>
                    Packs não são contados diretamente — a quantidade já é somada e contada no produto unitário correspondente.
                  </p>
                  <div className="estoqueTableWrapper">
                    <table className="estoqueTable balSimple">
                      <thead>
                        <tr>
                          <th>Descrição</th>
                          <th>Contado no unitário</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div style={{ fontWeight: 600, color: "var(--eco-ink)" }}>{item.descricao}</div>
                              <div style={{ fontSize: 11, color: "var(--eco-muted)", marginTop: 2 }}>
                                EAN: {item.sku} · {Number((item as any).packSize)} un/pack
                              </div>
                            </td>
                            <td style={{ color: "var(--eco-ink-2)" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <span style={{ color: "var(--eco-primary)", fontWeight: 700 }}>↳</span>
                                {baseDescricaoOf(item)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Non-Tracked Items Table */}
              <h3 style={{ margin: "32px 0 12px 0", fontSize: 18, color: "var(--eco-ink)" }}>Outros Produtos do Balanço (Não Rastreados)</h3>
              <div className="estoqueTableWrapper">
                <table className="estoqueTable balSimple">
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th style={{ width: 140, textAlign: "center" }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nonTrackedItems.length === 0 ? (
                      <tr>
                        <td colSpan={2} style={{ textAlign: "center", padding: 24, color: "var(--eco-muted)" }}>
                          Nenhum produto não rastreado neste balanço.
                        </td>
                      </tr>
                    ) : (
                      nonTrackedItems.map((item) => {
                        const isClosed = balance.status === "CLOSED";
                        return (
                          <tr key={item.id}>
                            <td>
                              <div style={{ fontWeight: 600, color: "var(--eco-ink)" }}>{item.descricao}</div>
                              <div style={{ fontSize: 11, color: "var(--eco-muted)", marginTop: 2 }}>EAN: {item.sku}</div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <button
                                className="estoqueBtn estoqueBtn--secondary"
                                style={{ height: 32, padding: "0 12px", fontSize: 12, borderColor: "var(--eco-primary)", color: "var(--eco-primary)" }}
                                disabled={isClosed}
                                onClick={() => handleToggleTracking(item.id, true)}
                              >
                                Rastrear Produto
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Modal Manual Occurrence */}
      {manualOccurOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setManualOccurOpen(false); }}>
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Lançar Ocorrência Manual</h2>
              <button className="estoqueModal__close" onClick={() => setManualOccurOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleAddManualOccurrence}>
              <div className="estoqueModal__body">
                <div className="estoqueFormGroup">
                  <label>EAN ou Cod Admin do Produto *</label>
                  <input
                    type="text"
                    className="estoqueInput"
                    value={manualEan}
                    onChange={(e) => setManualEan(e.target.value)}
                    required
                  />
                </div>

                <div className="estoqueFormGroup">
                  <label>Tipo de Ocorrência *</label>
                  <select
                    className="estoqueSelect"
                    value={manualType}
                    onChange={(e) => setManualType(e.target.value as AdjustmentType)}
                    required
                  >
                    <option value="AVARIA">Avaria</option>
                    <option value="VENCIMENTO">Vencimento</option>
                    <option value="CONSUMO">Consumo Interno</option>
                    <option value="DEVOLUCAO">Devolução</option>
                  </select>
                </div>

                <div className="estoqueFormGroup">
                  <label>Quantidade *</label>
                  <input
                    type="number"
                    className="estoqueInput"
                    min={1}
                    value={manualQty}
                    onChange={(e) => setManualQty(Math.max(1, Number(e.target.value)))}
                    required
                  />
                </div>

                <div className="estoqueFormGroup">
                  <label>Observação (Justificativa)</label>
                  <input
                    type="text"
                    className="estoqueInput"
                    value={manualObs}
                    onChange={(e) => setManualObs(e.target.value)}
                  />
                </div>
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setManualOccurOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  Registrar
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modal Teach Pack XML */}
      {teachPackOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setTeachPackOpen(false); }}>
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Ensinar XML de Entradas</h2>
              <button className="estoqueModal__close" onClick={() => setTeachPackOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleTeachPack}>
              <div className="estoqueModal__body">
                {/* Tabs */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button
                    type="button"
                    className={`estoqueBtn ${xmlIsPack ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setXmlIsPack(true)}
                  >
                    Vincular como Pack
                  </button>
                  <button
                    type="button"
                    className={`estoqueBtn ${!xmlIsPack ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setXmlIsPack(false)}
                  >
                    Não é Pack (Unitário)
                  </button>
                </div>

                <div className="estoqueFormGroup">
                  <label>EAN do Item *</label>
                  <input type="text" className="estoqueInput" value={packEan} disabled required />
                </div>

                {xmlIsPack ? (
                  <>
                    <div className="estoqueFormGroup">
                      <label>Qtd de Unidades no Pack (ex: 6 ou 12) *</label>
                      <input
                        type="number"
                        className="estoqueInput"
                        min={2}
                        value={packSize}
                        onChange={(e) => setPackSize(Math.max(2, Number(e.target.value)))}
                        required
                      />
                    </div>

                    <div className="estoqueFormGroup" style={{ background: "var(--eco-ground)", padding: 12, borderRadius: 8, border: "1px solid var(--eco-border-soft)" }}>
                      <label>Buscar Produto Unitário Base</label>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <input
                          type="text"
                          className="estoqueInput"
                          placeholder="Digite a descrição ou EAN do unitário..."
                          value={xmlSearchQuery}
                          onChange={(e) => setXmlSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearchXmlProduct(xmlSearchQuery);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="estoqueBtn estoqueBtn--secondary"
                          style={{ height: 40 }}
                          onClick={() => handleSearchXmlProduct(xmlSearchQuery)}
                        >
                          Buscar
                        </button>
                      </div>
                      
                      {xmlSearchLoading && <div style={{ fontSize: 12, color: "var(--eco-muted)", marginTop: 4 }}>Buscando...</div>}

                      {xmlSearchResults.length > 0 && (
                        <div style={{ maxHeight: 120, overflowY: "auto", background: "#ffffff", border: "1px solid var(--eco-border)", borderRadius: 6, marginTop: 8 }}>
                          {xmlSearchResults.map((prod) => (
                            <div
                              key={prod.id}
                              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid var(--eco-border-soft)" }}
                              onClick={() => {
                                setBaseProductId(prod.productId);
                                setXmlSearchResults([]);
                                setXmlSearchQuery("");
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--eco-ground)")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                            >
                              <strong>{prod.productId}</strong> — {prod.descricao} ({prod.numeroItem})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="estoqueFormGroup">
                      <label>EAN do Produto Base Unitário *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="EAN selecionado acima ou digite manualmente"
                        value={baseProductId}
                        onChange={(e) => setBaseProductId(e.target.value)}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="estoqueFormGroup">
                      <label>Descrição do Produto *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        value={xmlUnitDesc}
                        onChange={(e) => setXmlUnitDesc(e.target.value)}
                        required
                      />
                    </div>

                    <div className="estoqueFormGroup">
                      <label>Código ERP / Código Admin *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="Digite o código interno ERP/Admin"
                        value={xmlCodAdmin}
                        onChange={(e) => setXmlCodAdmin(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setTeachPackOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  {xmlIsPack ? "Vincular Pack" : "Cadastrar Unitário"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modal Teach Pack Saídas */}
      {teachSaidaOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setTeachSaidaOpen(false); }}>
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Ensinar Excel de Vendas</h2>
              <button className="estoqueModal__close" onClick={() => setTeachSaidaOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleTeachSaidaPack}>
              <div className="estoqueModal__body">
                {/* Tabs */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button
                    type="button"
                    className={`estoqueBtn ${saidaIsPack ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setSaidaIsPack(true)}
                  >
                    Vincular como Pack
                  </button>
                  <button
                    type="button"
                    className={`estoqueBtn ${!saidaIsPack ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setSaidaIsPack(false)}
                  >
                    Não é Pack (Unitário)
                  </button>
                </div>

                <div className="estoqueFormGroup">
                  <label>Cod Admin do Item *</label>
                  <input type="text" className="estoqueInput" value={saidaPackCod} disabled required />
                </div>

                {saidaIsPack ? (
                  <>
                    <div className="estoqueFormGroup">
                      <label>Qtd de Unidades no Pack (ex: 6 ou 12) *</label>
                      <input
                        type="number"
                        className="estoqueInput"
                        min={2}
                        value={saidaPackSize}
                        onChange={(e) => setSaidaPackSize(Math.max(2, Number(e.target.value)))}
                        required
                      />
                    </div>

                    <div className="estoqueFormGroup" style={{ background: "var(--eco-ground)", padding: 12, borderRadius: 8, border: "1px solid var(--eco-border-soft)" }}>
                      <label>Buscar Produto Unitário Base</label>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <input
                          type="text"
                          className="estoqueInput"
                          placeholder="Digite a descrição ou EAN do unitário..."
                          value={saidaSearchQuery}
                          onChange={(e) => setSaidaSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearchSaidaProduct(saidaSearchQuery);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="estoqueBtn estoqueBtn--secondary"
                          style={{ height: 40 }}
                          onClick={() => handleSearchSaidaProduct(saidaSearchQuery)}
                        >
                          Buscar
                        </button>
                      </div>
                      
                      {saidaSearchLoading && <div style={{ fontSize: 12, color: "var(--eco-muted)", marginTop: 4 }}>Buscando...</div>}

                      {saidaSearchResults.length > 0 && (
                        <div style={{ maxHeight: 120, overflowY: "auto", background: "#ffffff", border: "1px solid var(--eco-border)", borderRadius: 6, marginTop: 8 }}>
                          {saidaSearchResults.map((prod) => (
                            <div
                              key={prod.id}
                              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid var(--eco-border-soft)" }}
                              onClick={() => {
                                setSaidaBaseEan(prod.numeroItem);
                                setSaidaSearchResults([]);
                                setSaidaSearchQuery("");
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--eco-ground)")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                            >
                              <strong>{prod.numeroItem}</strong> — {prod.descricao} (EAN: {prod.productId})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="estoqueFormGroup">
                      <label>Cod Admin do Produto Base Unitário *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="Cod Admin selecionado acima ou digite"
                        value={saidaBaseEan}
                        onChange={(e) => setSaidaBaseEan(e.target.value)}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="estoqueFormGroup">
                      <label>Descrição do Produto *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        value={saidaUnitDesc}
                        onChange={(e) => setSaidaUnitDesc(e.target.value)}
                        required
                      />
                    </div>

                    <div className="estoqueFormGroup">
                      <label>EAN / SKU do Produto *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="Digite o EAN do produto"
                        value={saidaEan}
                        onChange={(e) => setSaidaEan(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setTeachSaidaOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  {saidaIsPack ? "Vincular Pack" : "Cadastrar Unitário"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modal Vincular De/Para de Ocorrência */}
      {teachOcorrenciaOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setTeachOcorrenciaOpen(false); }}>
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Vincular De/Para de Ocorrência</h2>
              <button className="estoqueModal__close" onClick={() => setTeachOcorrenciaOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleTeachOcorrencia}>
              <div className="estoqueModal__body">
                {/* Tabs */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button
                    type="button"
                    className={`estoqueBtn ${ocorrenciaMode === "link" ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setOcorrenciaMode("link")}
                  >
                    Vincular a Produto Existente
                  </button>
                  <button
                    type="button"
                    className={`estoqueBtn ${ocorrenciaMode === "create" ? "estoqueBtn--primary" : "estoqueBtn--secondary"}`}
                    style={{ flex: 1, height: 32, fontSize: 13 }}
                    onClick={() => setOcorrenciaMode("create")}
                  >
                    Cadastrar Produto Novo
                  </button>
                </div>

                <div className="estoqueFormGroup">
                  <label>Cod Admin do Item *</label>
                  <input type="text" className="estoqueInput" value={ocorrenciaCodAdmin} disabled required />
                </div>

                {ocorrenciaMode === "link" ? (
                  <>
                    <div className="estoqueFormGroup" style={{ background: "var(--eco-ground)", padding: 12, borderRadius: 8, border: "1px solid var(--eco-border-soft)" }}>
                      <label>Buscar Produto no De/Para</label>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <input
                          type="text"
                          className="estoqueInput"
                          placeholder="Digite a descrição ou EAN do produto..."
                          value={ocorrenciaSearchQuery}
                          onChange={(e) => setOcorrenciaSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearchOcorrenciaProduct(ocorrenciaSearchQuery);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="estoqueBtn estoqueBtn--secondary"
                          style={{ height: 40 }}
                          onClick={() => handleSearchOcorrenciaProduct(ocorrenciaSearchQuery)}
                        >
                          Buscar
                        </button>
                      </div>

                      {ocorrenciaSearchLoading && <div style={{ fontSize: 12, color: "var(--eco-muted)", marginTop: 4 }}>Buscando...</div>}

                      {ocorrenciaSearchResults.length > 0 && (
                        <div style={{ maxHeight: 120, overflowY: "auto", background: "#ffffff", border: "1px solid var(--eco-border)", borderRadius: 6, marginTop: 8 }}>
                          {ocorrenciaSearchResults.map((prod) => (
                            <div
                              key={prod.id}
                              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid var(--eco-border-soft)" }}
                              onClick={() => {
                                setOcorrenciaSelectedRef(prod);
                                setOcorrenciaSearchResults([]);
                                setOcorrenciaSearchQuery("");
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--eco-ground)")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                            >
                              <strong>{prod.descricao}</strong> (EAN: {prod.productId})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="estoqueFormGroup">
                      <label>Produto Selecionado *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="Busque e selecione o produto acima"
                        value={ocorrenciaSelectedRef ? `${ocorrenciaSelectedRef.descricao} (EAN: ${ocorrenciaSelectedRef.productId})` : ""}
                        readOnly
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="estoqueFormGroup">
                      <label>Descrição do Produto *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        value={ocorrenciaDesc}
                        onChange={(e) => setOcorrenciaDesc(e.target.value)}
                        required
                      />
                    </div>

                    <div className="estoqueFormGroup">
                      <label>EAN / SKU do Produto *</label>
                      <input
                        type="text"
                        className="estoqueInput"
                        placeholder="Digite o EAN do produto"
                        value={ocorrenciaEan}
                        onChange={(e) => setOcorrenciaEan(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setTeachOcorrenciaOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  {ocorrenciaMode === "link" ? "Vincular Produto" : "Cadastrar Produto"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Vendas sem produto cadastrado (não casadas) */}
      {duplicateOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setDuplicateOpen(false); }}>
          <div className="estoqueModal" style={{ maxWidth: 600 }}>
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Vendas sem produto cadastrado</h2>
              <button className="estoqueModal__close" onClick={() => setDuplicateOpen(false)}>
                &times;
              </button>
            </header>

            <div className="estoqueModal__body" style={{ maxHeight: 400, overflowY: "auto" }}>
              <p style={{ fontSize: 13, color: "var(--eco-muted)", margin: "0 0 8px 0" }}>
                Estas linhas de venda não têm produto correspondente no cadastro e não entraram no balanço.
                Cadastre os produtos e reimporte, ou dispense o aviso.
              </p>

              {(Array.isArray(balance?.saidaErrosDePara) ? (balance!.saidaErrosDePara as any[]) : []).map(
                (e: { codAdmin?: string; produto?: string; qty?: number }, i: number) => (
                  <div key={`${e.codAdmin ?? "?"}-${i}`} style={{ borderBottom: "1px solid var(--eco-border-soft)", padding: "10px 0", display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--eco-ink)" }}>
                        {e.produto || "Produto desconhecido"}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--eco-muted)", marginTop: 2 }}>
                        Cód. Admin: {e.codAdmin ?? "—"}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--eco-ink)", fontWeight: 600, whiteSpace: "nowrap" }}>
                      Qtd: {e.qty ?? 0}
                    </div>
                  </div>
                ),
              )}
            </div>

            <footer className="estoqueModal__footer">
              <button
                type="button"
                className="estoqueBtn estoqueBtn--secondary"
                onClick={() => setDuplicateOpen(false)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="estoqueBtn estoqueBtn--primary"
                onClick={async () => { await handleDismissSaidaErros(); setDuplicateOpen(false); }}
              >
                Dispensar aviso
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Modal View Ajuste Justificativa */}
      {ajusteModalOpen && selectedAjusteItem && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setAjusteModalOpen(false); }}>
          <div className="estoqueModal" style={{ maxWidth: 450 }}>
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Justificativa do Ajuste de Estoque</h2>
              <button className="estoqueModal__close" onClick={() => setAjusteModalOpen(false)}>
                &times;
              </button>
            </header>

            <div className="estoqueModal__body">
              <div style={{ marginBottom: 16 }}>
                <strong style={{ fontSize: 13, color: "var(--eco-muted)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Tipo</strong>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--eco-ink)" }}>
                  {AJUSTE_TIPO_LABELS[(selectedAjusteItem.ajusteJustificativaTipo || "EM_ANALISE") as AjusteEstoqueTipo]}
                </p>
              </div>

              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 13, color: "var(--eco-muted)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Justificativa</strong>
                <p style={{ margin: 0, fontSize: 15, color: "var(--eco-ink-2)" }}>
                  {selectedAjusteItem.ajusteJustificativaTexto || "Nenhuma justificativa informada."}
                </p>
              </div>
            </div>

            <footer className="estoqueModal__footer" style={{ display: "flex", justifyContent: "center" }}>
              <button
                type="button"
                className="estoqueBtn estoqueBtn--secondary"
                style={{ width: "100%", height: 40 }}
                onClick={() => setAjusteModalOpen(false)}
              >
                Fechar
              </button>
            </footer>
          </div>
        </div>
      )}

      <EcoConfirmDialog
        open={confirmClose}
        title="Encerrar o balanço?"
        message="O ciclo de balanço diário da loja é encerrado e as contagens não mudam mais."
        confirmLabel="Encerrar balanço"
        tone="danger"
        busy={closing}
        onConfirm={handleCloseBalance}
        onCancel={() => setConfirmClose(false)}
      />
    </div>
  );
}
